import { useEffect, useMemo, useRef, useState } from "react";
import AdminPageHeader from "@/components/admin/AdminPageHeader";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import {
    Plus,
    Trash2,
    GripVertical,
    Loader2,
    X,
    Lock,
    ClipboardList,
    Pencil,
} from "lucide-react";
import { toast } from "sonner";
import { api, apiErrorMessage } from "@/lib/api";
import { formatDDMMYYYY } from "@/lib/formatDate";

const FIELD_TYPES = [
    { value: "text", label: "Short text" },
    { value: "textarea", label: "Long text" },
    { value: "email", label: "Email" },
    { value: "phone", label: "Phone" },
    { value: "url", label: "URL / Link" },
    { value: "file", label: "File upload" },
    { value: "date", label: "Date" },
    { value: "boolean", label: "Yes / No" },
    { value: "select", label: "Dropdown" },
    { value: "degree", label: "Degree Dropdown" },
];

const OTHERS = "Others";

let nextId = 1;
const makeField = (overrides = {}) => ({
    id: `f-${nextId++}`,
    label: "",
    type: "text",
    required: false,
    options: [],
    _optionDraft: "",
    ...overrides,
});

function ensureOthers(options) {
    const filtered = (options || []).filter(
        (o) => o.toLowerCase() !== OTHERS.toLowerCase(),
    );
    return [...filtered, OTHERS];
}

const defaultFields = () => [
    makeField({ label: "Full name", type: "text", required: true }),
    makeField({ label: "Email", type: "email", required: true }),
];

export default function CreateForm() {
    const [forms, setForms] = useState([]);
    const [roles, setRoles] = useState([]);
    const [jds, setJds] = useState([]);
    const [loading, setLoading] = useState(true);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);

    const [formName, setFormName] = useState("");
    const [jobRoleId, setJobRoleId] = useState("");
    const [jobDescriptionId, setJobDescriptionId] = useState("");
    const [fields, setFields] = useState(defaultFields());
    const [submitting, setSubmitting] = useState(false);

    const [deleteTarget, setDeleteTarget] = useState(null);

    const roleMap = useMemo(() => {
        const m = {};
        roles.forEach((r) => (m[r.id] = r));
        return m;
    }, [roles]);
    const jdMap = useMemo(() => {
        const m = {};
        jds.forEach((j) => (m[j.id] = j));
        return m;
    }, [jds]);
    const jdsForRole = useMemo(
        () => jds.filter((j) => j.job_role_id === jobRoleId),
        [jds, jobRoleId],
    );

    const fetchAll = async () => {
        try {
            const [rolesRes, formsRes, jdsRes] = await Promise.all([
                api.get("/job-roles"),
                api.get("/forms"),
                api.get("/job-descriptions"),
            ]);
            setRoles(rolesRes.data);
            setForms(formsRes.data);
            setJds(jdsRes.data);
        } catch (e) {
            toast.error(apiErrorMessage(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAll();
    }, []);

    const openCreate = () => {
        setEditingId(null);
        setFormName("");
        setJobRoleId("");
        setJobDescriptionId("");
        setFields(defaultFields());
        setDialogOpen(true);
    };

    const openEdit = (form) => {
        setEditingId(form.id);
        setFormName(form.form_name || "");
        setJobRoleId(form.job_role_id || "");
        setJobDescriptionId(form.job_description_id || "");
        setFields(
            (form.fields || []).map((f) =>
                makeField({
                    label: f.label,
                    type: f.type,
                    required: !!f.required,
                    options: f.options || [],
                }),
            ),
        );
        setDialogOpen(true);
    };

    const closeDialog = () => {
        setDialogOpen(false);
        setEditingId(null);
        setFormName("");
        setJobRoleId("");
        setJobDescriptionId("");
        setFields(defaultFields());
    };

    // Reset JD when role changes so we don't leave a stale JD from another role.
    const onRoleChange = (v) => {
        setJobRoleId(v);
        // Preserve JD only if it belongs to the new role
        const stillValid = jds.find(
            (j) => j.id === jobDescriptionId && j.job_role_id === v,
        );
        if (!stillValid) setJobDescriptionId("");
    };

    const addField = () => setFields((fs) => [...fs, makeField()]);
    const removeField = (id) =>
        setFields((fs) => fs.filter((f) => f.id !== id));

    const dragIdxRef = useRef(null);
    const reorderFields = (from, to) => {
        if (from == null || from === to) return;
        setFields((arr) => {
            const next = [...arr];
            const [moved] = next.splice(from, 1);
            next.splice(to, 0, moved);
            return next;
        });
    };

    const updateField = (id, patch) =>
        setFields((fs) =>
            fs.map((f) => {
                if (f.id !== id) return f;
                const merged = { ...f, ...patch };
                if (patch.type !== undefined) {
                    if (merged.type === "select") {
                        merged.options = ensureOthers(merged.options || []);
                    } else {
                        merged.options = [];
                    }
                }
                return merged;
            }),
        );

    const addOption = (id) =>
        setFields((fs) =>
            fs.map((f) => {
                if (f.id !== id) return f;
                const draft = (f._optionDraft || "").trim();
                if (!draft) {
                    toast.error("Option cannot be empty.");
                    return f;
                }
                if (draft.toLowerCase() === OTHERS.toLowerCase()) {
                    toast.error(
                        "'Others' is added automatically as the last option.",
                    );
                    return { ...f, _optionDraft: "" };
                }
                const exists = (f.options || []).some(
                    (o) => o.toLowerCase() === draft.toLowerCase(),
                );
                if (exists) {
                    toast.error("This option already exists.");
                    return f;
                }
                const nextOptions = ensureOthers([
                    ...(f.options || []).filter(
                        (o) => o.toLowerCase() !== OTHERS.toLowerCase(),
                    ),
                    draft,
                ]);
                return { ...f, options: nextOptions, _optionDraft: "" };
            }),
        );

    const removeOption = (id, opt) =>
        setFields((fs) =>
            fs.map((f) => {
                if (f.id !== id) return f;
                if (opt.toLowerCase() === OTHERS.toLowerCase()) return f;
                const options = ensureOthers(
                    (f.options || []).filter((o) => o !== opt),
                );
                return { ...f, options };
            }),
        );

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!formName.trim()) {
            toast.error("Give the form a name first.");
            return;
        }
        if (!jobRoleId) {
            toast.error("Please select a Job Role.");
            return;
        }
        if (!jobDescriptionId) {
            toast.error("Please select a Job Description.");
            return;
        }
        if (fields.length === 0) {
            toast.error("Add at least one field.");
            return;
        }
        for (const f of fields) {
            if (f.type === "select") {
                const nonOthers = (f.options || []).filter(
                    (o) => o.toLowerCase() !== OTHERS.toLowerCase(),
                );
                if (nonOthers.length === 0) {
                    toast.error(
                        `Dropdown field "${
                            f.label || "(unnamed)"
                        }" needs at least one option besides "Others".`,
                    );
                    return;
                }
            }
        }
        setSubmitting(true);
        try {
            const payload = {
                form_name: formName.trim(),
                job_role_id: jobRoleId,
                job_description_id: jobDescriptionId,
                fields: fields.map((f) => ({
                    label: f.label,
                    type: f.type,
                    required: f.required,
                    options: f.type === "select" ? ensureOthers(f.options) : [],
                })),
            };
            if (editingId) {
                const { data } = await api.put(
                    `/forms/${editingId}`,
                    payload,
                );
                setForms((fs) =>
                    fs.map((f) => (f.id === editingId ? data : f)),
                );
                toast.success(`Form "${formName}" updated.`);
            } else {
                const { data } = await api.post("/forms", payload);
                setForms((fs) => [data, ...fs]);
                toast.success(`Form "${formName}" saved.`);
            }
            closeDialog();
        } catch (err) {
            toast.error(apiErrorMessage(err));
        } finally {
            setSubmitting(false);
        }
    };

    const doDelete = async () => {
        if (!deleteTarget) return;
        try {
            await api.delete(`/forms/${deleteTarget.id}`);
            setForms((fs) => fs.filter((f) => f.id !== deleteTarget.id));
            toast.success("Form deleted.");
        } catch (err) {
            toast.error(apiErrorMessage(err));
        } finally {
            setDeleteTarget(null);
        }
    };

    return (
        <div data-testid="admin-form-builder-page">
            <AdminPageHeader
                eyebrow="Modules / Application Forms"
                title="Application Forms"
                description="One form per job description. Add, edit or delete forms candidates fill in when applying."
                actions={
                    <button
                        onClick={openCreate}
                        data-testid="form-add-button"
                        className="inline-flex items-center gap-2 px-5 h-11 bg-[#0F1F38] text-[#F7F5F0] text-xs tracking-label uppercase font-semibold hover:bg-[#1A3254] transition-colors"
                    >
                        <Plus size={14} /> Add Form
                    </button>
                }
            />

            <div className="max-w-6xl mx-auto px-6 md:px-10 py-10">
                {loading ? (
                    <div className="flex items-center gap-3 text-sm text-[#4B5563]">
                        <Loader2 size={16} className="animate-spin" />
                        Loading forms…
                    </div>
                ) : forms.length === 0 ? (
                    <div
                        data-testid="forms-empty"
                        className="bg-white border border-dashed border-[#0F1F38]/20 py-20 px-8 text-center flex flex-col items-center"
                    >
                        <div className="w-12 h-12 flex items-center justify-center bg-[#F7F5F0] border border-[#0F1F38]/10 mb-6">
                            <ClipboardList
                                size={20}
                                strokeWidth={1.5}
                                className="text-[#0F1F38]"
                            />
                        </div>
                        <div className="font-display text-3xl leading-tight text-[#0F1F38]">
                            No application forms yet.
                        </div>
                        <button
                            onClick={openCreate}
                            data-testid="form-add-empty"
                            className="mt-8 inline-flex items-center gap-2 px-5 h-11 bg-[#0F1F38] text-[#F7F5F0] text-xs tracking-label uppercase font-semibold hover:bg-[#1A3254] transition-colors"
                        >
                            <Plus size={14} /> Add Form
                        </button>
                    </div>
                ) : (
                    <div
                        data-testid="forms-grid"
                        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
                    >
                        {forms.map((f) => (
                            <div
                                key={f.id}
                                data-testid={`form-card-${f.id}`}
                                className="bg-white border border-[#0F1F38]/10 p-6 flex flex-col gap-4 hover:border-[#0F1F38]/40 transition-colors"
                            >
                                <div className="w-10 h-10 flex items-center justify-center bg-[#F7F5F0] border border-[#0F1F38]/10">
                                    <ClipboardList
                                        size={16}
                                        strokeWidth={1.5}
                                        className="text-[#0F1F38]"
                                    />
                                </div>
                                <div className="font-display text-2xl leading-tight text-[#0F1F38] break-words">
                                    {f.form_name}
                                </div>
                                <div className="text-[10px] tracking-label uppercase text-[#0F1F38]/60">
                                    {roleMap[f.job_role_id]?.name ||
                                        "Unknown role"}
                                </div>
                                {f.job_description_id && (
                                    <div className="text-xs text-[#4B5563] -mt-2">
                                        {jdMap[f.job_description_id]
                                            ?.job_title || "Unknown JD"}
                                    </div>
                                )}
                                <div className="mt-auto flex items-center flex-wrap gap-2 pt-2 border-t border-[#0F1F38]/10">
                                    <div className="text-xs text-[#4B5563] flex-1">
                                        {(f.fields || []).length} field
                                        {(f.fields || []).length === 1
                                            ? ""
                                            : "s"}{" "}
                                        · {formatDDMMYYYY(f.created_at)}
                                    </div>
                                    <button
                                        onClick={() => openEdit(f)}
                                        data-testid={`form-edit-${f.id}`}
                                        className="inline-flex items-center gap-1 px-3 h-8 border border-[#0F1F38]/20 hover:border-[#0F1F38] text-[10px] tracking-label uppercase font-semibold transition-colors"
                                    >
                                        <Pencil size={11} /> Edit
                                    </button>
                                    <button
                                        onClick={() => setDeleteTarget(f)}
                                        data-testid={`form-delete-${f.id}`}
                                        className="inline-flex items-center gap-1 px-3 h-8 border border-[#0F1F38]/20 hover:border-red-700 hover:text-red-700 text-[10px] tracking-label uppercase font-semibold transition-colors"
                                    >
                                        <Trash2 size={11} /> Delete
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Create / Edit dialog */}
            <Dialog
                open={dialogOpen}
                onOpenChange={(o) => (o ? setDialogOpen(true) : closeDialog())}
            >
                <DialogContent
                    data-testid="form-dialog"
                    className="bg-[#F7F5F0] border-[#0F1F38]/20 rounded-none max-w-4xl max-h-[90vh] overflow-y-auto"
                >
                    <DialogHeader>
                        <div className="text-[10px] tracking-label uppercase text-[#0F1F38]/60">
                            {editingId ? "Edit form" : "New form"}
                        </div>
                        <DialogTitle className="font-display text-3xl leading-tight text-[#0F1F38]">
                            {editingId
                                ? "Edit application form"
                                : "Create an application form"}
                        </DialogTitle>
                        <DialogDescription className="text-sm text-[#4B5563]">
                            Choose the role and job description, then add the
                            fields candidates should fill in.
                        </DialogDescription>
                    </DialogHeader>

                    <form
                        onSubmit={onSubmit}
                        data-testid="form-builder"
                        className="space-y-6"
                    >
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="bg-white border border-[#0F1F38]/10 p-4 md:p-5">
                                <Label className="text-[10px] tracking-label uppercase text-[#0F1F38]/60">
                                    Form name
                                </Label>
                                <Input
                                    value={formName}
                                    onChange={(e) =>
                                        setFormName(e.target.value)
                                    }
                                    placeholder="e.g. Backend Engineer — Bengaluru"
                                    data-testid="input-form-name"
                                    className="mt-2 h-11 rounded-none border-[#0F1F38]/20"
                                />
                            </div>

                            <div className="bg-white border border-[#0F1F38]/10 p-4 md:p-5">
                                <Label className="text-[10px] tracking-label uppercase text-[#0F1F38]/60">
                                    Job Role
                                </Label>
                                <Select
                                    value={jobRoleId}
                                    onValueChange={onRoleChange}
                                >
                                    <SelectTrigger
                                        data-testid="input-form-job-role"
                                        className="mt-2 h-11 rounded-none border-[#0F1F38]/20"
                                    >
                                        <SelectValue
                                            placeholder={
                                                roles.length === 0
                                                    ? "No roles yet"
                                                    : "Select a job role"
                                            }
                                        />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {roles.map((r) => (
                                            <SelectItem
                                                key={r.id}
                                                value={r.id}
                                                data-testid={`form-role-option-${r.id}`}
                                            >
                                                {r.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="bg-white border border-[#0F1F38]/10 p-4 md:p-5">
                                <Label className="text-[10px] tracking-label uppercase text-[#0F1F38]/60">
                                    Job Description
                                </Label>
                                <Select
                                    value={jobDescriptionId}
                                    onValueChange={setJobDescriptionId}
                                    disabled={!jobRoleId}
                                >
                                    <SelectTrigger
                                        data-testid="input-form-job-description"
                                        className="mt-2 h-11 rounded-none border-[#0F1F38]/20"
                                    >
                                        <SelectValue
                                            placeholder={
                                                !jobRoleId
                                                    ? "Select a role first"
                                                    : jdsForRole.length === 0
                                                      ? "No JDs for this role"
                                                      : "Select a job description"
                                            }
                                        />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {jdsForRole.map((j) => (
                                            <SelectItem
                                                key={j.id}
                                                value={j.id}
                                                data-testid={`form-jd-option-${j.id}`}
                                            >
                                                {j.job_title}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div>
                            <div className="flex items-center justify-between mb-3">
                                <div className="text-[10px] tracking-label uppercase text-[#0F1F38]/60">
                                    Fields · {fields.length}
                                </div>
                                <button
                                    type="button"
                                    onClick={addField}
                                    data-testid="add-field-button"
                                    className="inline-flex items-center gap-2 px-4 h-10 border border-[#0F1F38]/20 hover:border-[#0F1F38] text-xs tracking-label uppercase font-semibold transition-colors bg-white"
                                >
                                    <Plus size={14} /> Add field
                                </button>
                            </div>
                            <div className="space-y-3">
                                {fields.map((f, idx) => (
                                    <div
                                        key={f.id}
                                        data-testid={`field-row-${idx}`}
                                        draggable
                                        onDragStart={(e) => {
                                            dragIdxRef.current = idx;
                                            e.dataTransfer.effectAllowed =
                                                "move";
                                        }}
                                        onDragOver={(e) => e.preventDefault()}
                                        onDrop={(e) => {
                                            e.preventDefault();
                                            reorderFields(
                                                dragIdxRef.current,
                                                idx,
                                            );
                                            dragIdxRef.current = null;
                                        }}
                                        className="bg-white border border-[#0F1F38]/10 p-4"
                                    >
                                        <div className="grid grid-cols-12 gap-3 items-center">
                                            <div className="col-span-1 flex items-center justify-center text-[#0F1F38]/40 cursor-grab active:cursor-grabbing">
                                                <GripVertical size={16} />
                                            </div>
                                            <div className="col-span-12 md:col-span-5">
                                                <Input
                                                    value={f.label}
                                                    onChange={(e) =>
                                                        updateField(f.id, {
                                                            label: e.target
                                                                .value,
                                                        })
                                                    }
                                                    placeholder="Field label"
                                                    data-testid={`field-label-${idx}`}
                                                    className="h-10 rounded-none border-[#0F1F38]/20"
                                                />
                                            </div>
                                            <div className="col-span-8 md:col-span-3">
                                                <Select
                                                    value={f.type}
                                                    onValueChange={(v) =>
                                                        updateField(f.id, {
                                                            type: v,
                                                        })
                                                    }
                                                >
                                                    <SelectTrigger
                                                        data-testid={`field-type-${idx}`}
                                                        className="h-10 rounded-none border-[#0F1F38]/20"
                                                    >
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {FIELD_TYPES.map((t) => (
                                                            <SelectItem
                                                                key={t.value}
                                                                value={t.value}
                                                            >
                                                                {t.label}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                            <div className="col-span-3 md:col-span-2 flex items-center gap-2">
                                                <Switch
                                                    checked={f.required}
                                                    onCheckedChange={(v) =>
                                                        updateField(f.id, {
                                                            required: v,
                                                        })
                                                    }
                                                    data-testid={`field-required-${idx}`}
                                                />
                                                <span className="text-xs text-[#4B5563]">
                                                    Required
                                                </span>
                                            </div>
                                            <div className="col-span-1 flex justify-end">
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        removeField(f.id)
                                                    }
                                                    data-testid={`field-remove-${idx}`}
                                                    className="p-2 text-[#0F1F38]/50 hover:text-red-700 transition-colors"
                                                    aria-label="Remove field"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </div>

                                        {f.type === "select" && (
                                            <div
                                                data-testid={`options-builder-${idx}`}
                                                className="mt-4 pt-4 border-t border-[#0F1F38]/10 space-y-3"
                                            >
                                                <div className="text-[10px] tracking-label uppercase text-[#0F1F38]/60">
                                                    Dropdown options
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <Input
                                                        value={
                                                            f._optionDraft || ""
                                                        }
                                                        onChange={(e) =>
                                                            updateField(f.id, {
                                                                _optionDraft:
                                                                    e.target
                                                                        .value,
                                                            })
                                                        }
                                                        onKeyDown={(e) => {
                                                            if (
                                                                e.key ===
                                                                "Enter"
                                                            ) {
                                                                e.preventDefault();
                                                                addOption(
                                                                    f.id,
                                                                );
                                                            }
                                                        }}
                                                        placeholder="Search / add option"
                                                        data-testid={`option-input-${idx}`}
                                                        className="h-10 rounded-none border-[#0F1F38]/20"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            addOption(f.id)
                                                        }
                                                        data-testid={`option-add-${idx}`}
                                                        className="inline-flex items-center justify-center w-10 h-10 bg-[#0F1F38] text-[#F7F5F0] hover:bg-[#1A3254] transition-colors"
                                                        aria-label="Add option"
                                                    >
                                                        <Plus size={14} />
                                                    </button>
                                                </div>
                                                <ul
                                                    data-testid={`options-list-${idx}`}
                                                    className="flex flex-wrap gap-2"
                                                >
                                                    {(f.options || []).map(
                                                        (o) => {
                                                            const isOthers =
                                                                o.toLowerCase() ===
                                                                OTHERS.toLowerCase();
                                                            return (
                                                                <li
                                                                    key={o}
                                                                    data-testid={`option-item-${idx}-${o}`}
                                                                    className={`inline-flex items-center gap-2 px-3 h-8 border ${
                                                                        isOthers
                                                                            ? "border-[#0F1F38]/40 bg-[#EAE5D9]"
                                                                            : "border-[#0F1F38]/20 bg-white"
                                                                    } text-xs text-[#0F1F38]`}
                                                                >
                                                                    <span>
                                                                        {o}
                                                                    </span>
                                                                    {isOthers ? (
                                                                        <Lock
                                                                            size={
                                                                                11
                                                                            }
                                                                            className="text-[#0F1F38]/50"
                                                                        />
                                                                    ) : (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() =>
                                                                                removeOption(
                                                                                    f.id,
                                                                                    o,
                                                                                )
                                                                            }
                                                                            data-testid={`option-remove-${idx}-${o}`}
                                                                            className="text-[#0F1F38]/50 hover:text-red-700 transition-colors"
                                                                            aria-label={`Remove ${o}`}
                                                                        >
                                                                            <X
                                                                                size={
                                                                                    12
                                                                                }
                                                                            />
                                                                        </button>
                                                                    )}
                                                                </li>
                                                            );
                                                        },
                                                    )}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="flex items-center justify-end gap-3 pt-3 border-t border-[#0F1F38]/10">
                            <button
                                type="button"
                                onClick={closeDialog}
                                data-testid="form-dialog-cancel"
                                className="px-5 h-11 text-xs tracking-label uppercase font-semibold border border-[#0F1F38]/20 hover:border-[#0F1F38] transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                data-testid="form-builder-submit"
                                disabled={submitting}
                                className="px-6 h-11 bg-[#0F1F38] text-[#F7F5F0] text-xs tracking-label uppercase font-semibold hover:bg-[#1A3254] transition-colors disabled:opacity-60 inline-flex items-center gap-2"
                            >
                                {submitting && (
                                    <Loader2
                                        size={14}
                                        className="animate-spin"
                                    />
                                )}
                                {editingId ? "Save" : "Create"}
                            </button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Delete confirm */}
            <Dialog
                open={!!deleteTarget}
                onOpenChange={(o) => !o && setDeleteTarget(null)}
            >
                <DialogContent
                    data-testid="form-delete-dialog"
                    className="bg-[#F7F5F0] border-[#0F1F38]/20 rounded-none max-w-md"
                >
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl leading-tight text-[#0F1F38]">
                            Delete form?
                        </DialogTitle>
                        <DialogDescription className="text-sm text-[#0F1F38]/80 pt-2">
                            This will permanently remove{" "}
                            <strong>{deleteTarget?.form_name}</strong>. This
                            cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="flex items-center justify-end gap-3 pt-4">
                        <button
                            onClick={() => setDeleteTarget(null)}
                            data-testid="form-delete-cancel"
                            className="px-5 h-11 text-xs tracking-label uppercase font-semibold border border-[#0F1F38]/20 hover:border-[#0F1F38] transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={doDelete}
                            data-testid="form-delete-confirm"
                            className="px-6 h-11 bg-red-700 text-white text-xs tracking-label uppercase font-semibold hover:bg-red-800 transition-colors"
                        >
                            Delete
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
